import { useCallback, useEffect, useRef, useState } from "react";
import Head from "next/head";
import { cloneDeep } from "lodash";
import Page from "../layouts/main";
import Context from "../components/context/context";
import CTA from "../components/CTA/cta.module";
import Footer from "../components/Footer/footer.module";
import Picture from "../components/picture/picture.module";
import InputField from "../components/Form/InputField/inputfield.module";
import Radio from "../components/Form/Radio/radio.module";
import TextArea from "../components/Form/TextArea/textarea.module";
import styles from "../styles/modules/contact-us.module.scss";
import settings from "../util/settings";
import { useMemo, useContext } from "react";
import { DEFAULT_LOCATION, formatAddress } from "../util/locations";
import { trackContactFormEvent } from "../util/conversion-events/contact-form-events";

const ContactUs = () => {
  const { location } = useContext(Context);
  const locationToUse = useMemo(
    () => (location ? location : DEFAULT_LOCATION),
    [location]
  );
  const formattedAddress = useMemo(
    () => formatAddress(locationToUse.address),
    [locationToUse]
  );

  const [showError, setShowError] = useState({
    fname: false,
    lname: false,
    email: false,
    phone: false,
    subject: false,
    message: false,
    textoptin: false,
  });
  const [inputValues, setInputValues] = useState({});
  const [emailState, setEmailState] = useState({
    submitted: false,
    sent: false,
    failed: false,
  });
  const [analytics, setAnalytics] = useState({});

  const page = useRef();
  const inputs = [
    useRef(),
    useRef(),
    useRef(),
    useRef(),
    useRef(),
    useRef(),
    useRef(),
  ];

  useEffect(() => {
    if (page.current) {
      setAnalytics(page.current.analytics);
    }
  }, []);

  const handleChange = useCallback(
    (e, input, value) => {
      setInputValues({ ...inputValues, [input.props.id]: value });
      setShowError({ ...showError, [input.props.id]: true });
    },
    [inputValues, showError]
  );

  const handleRadioChange = useCallback(
    (e, id, value) => {
      setInputValues({ ...inputValues, [id]: value });
    },
    [inputValues]
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      for (let i = 0; i < inputs.length; i++) {
        if (inputs[i].current.state && !inputs[i].current.state.valid) {
          let showErrorClone = { ...showError };
          Object.keys(showErrorClone).forEach((key) => {
            showErrorClone[key] = true;
          });
          setShowError(showErrorClone);
          return;
        }
      }

      let emailStateClone = cloneDeep(emailState);
      emailStateClone.failed = false;
      emailStateClone.submitted = true;
      setEmailState(emailStateClone);

      const formData = new URLSearchParams();
      formData.append("locationId", locationToUse.id);
      for (var k in inputValues) {
        formData.append(k, inputValues[k]);
      }

      fetch("/forms/contact-form.php", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-type": "application/x-www-form-urlencoded",
        },
        body: formData,
      })
        .then((response) => {
          if (!response.ok) {
            emailStateClone.failed = true;
          } else {
            emailStateClone.sent = true;
          }
          return response.json();
        })
        .then((data) => {
          // if (settings.isDev) {
          //   console.log(data)
          //   document.open();
          //   document.write(data);
          // }
          trackCTA(true);
          trackContactFormEvent();
          setTimeout(() => setEmailState({ ...emailStateClone }), 500);
        })
        .catch((msg) => {
          trackCTA(false);
          emailStateClone.failed = true;
          setTimeout(() => setEmailState({ ...emailStateClone }), 2000);
        });
    },
    [emailState, showError, inputValues, analytics]
  );

  const trackCTA = useCallback(
    (successful) => {
      analytics.event({
        category: "contact-form",
        action: "form-submission",
        label: successful ? "successful" : "failed",
      });
    },
    [analytics]
  );

  return (
    <Page id={styles["page-contact-us"]} ref={page}>
      <Head>
        <title>Contact Us - Kudos Golf</title>
      </Head>
      <section className={`section-content ${styles["section-contact-form"]}`}>
        <h2 className={styles.header}>Contact Us</h2>

        {(!emailState.submitted ||
          (emailState.submitted && !emailState.sent)) && (
          <form onSubmit={handleSubmit} autoComplete="true">
            <div className="row">
              <div className="col-md-4 mt-3">
                <InputField
                  autoComplete="given-name"
                  maxLength={200}
                  ref={inputs[0]}
                  id="fname"
                  name="fname"
                  label="First Name"
                  type="text"
                  isRequired
                  value={inputValues.fname}
                  placeholder="John"
                  showError={showError.fname}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 mt-3">
                <InputField
                  autoComplete="family-name"
                  maxLength={200}
                  ref={inputs[1]}
                  id="lname"
                  label="Last Name"
                  type="text"
                  isRequired
                  value={inputValues.lname}
                  placeholder="Smith"
                  showError={showError.lname}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 mt-3">
                <InputField
                  autoComplete="email"
                  maxLength={200}
                  ref={inputs[2]}
                  id="email"
                  label="E-mail"
                  type="email"
                  isRequired
                  value={inputValues.email}
                  placeholder="johnsmith@gmail.com"
                  showError={showError.email}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-4 mt-3">
                <InputField
                  autoComplete="tel"
                  maxLength={200}
                  ref={inputs[3]}
                  id="phone"
                  label="Phone"
                  type="phone"
                  value={inputValues.phone}
                  placeholder="(123) 456 - 7890"
                  showError={showError.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-8 mt-3">
                <InputField
                  maxLength={200}
                  ref={inputs[4]}
                  id="subject"
                  label="Subject"
                  type="text"
                  isRequired
                  value={inputValues.subject}
                  placeholder="e.g. Booking inquiry"
                  showError={showError.subject}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-8 mt-3">
                <TextArea
                  maxLength={500}
                  ref={inputs[5]}
                  id="message"
                  label="Message"
                  isRequired
                  value={inputValues.subject}
                  placeholder="Your inquiry here"
                  showError={showError.message}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-8 mt-3">
                <Radio
                  ref={inputs[6]}
                  id="textoptin"
                  label="Do you agree to opt-in to receive text messages?"
                  isRequired
                  value={inputValues.textoptin}
                  defaultOptIndex={0}
                  options={["I agree to opt-in", "I do not agree to opt-in"]}
                  showError={showError.message}
                  onChange={handleRadioChange}
                />
              </div>
            </div>

            {(!emailState.submitted ||
              (emailState.submitted && emailState.failed)) && (
              <div className="mt-4">
                <button className="button" type="submit">
                  Send message
                </button>
              </div>
            )}
          </form>
        )}
        {emailState.submitted && (
          <div className="mt-5">
            {emailState.sent && (
              <>
                <p className="mb-0">
                  <strong>Thank you for your message!</strong>
                </p>
                <p>
                  A member of our team will be in touch with you as soon as
                  possible.
                </p>
              </>
            )}
            {emailState.failed && (
              <p>
                Sorry, there was an error sending your email. Please try again
                later.
              </p>
            )}
            {!emailState.sent && !emailState.failed && <p>Sending email...</p>}
          </div>
        )}
      </section>

      <section className={`section-content ${styles["section-location"]}`}>
        <h2 className={styles["location-header"]}>Our Location</h2>

        <div className="row">
          <p
            className={`col-sm-6 col-md-4 mt-3 text-body-large ${styles["location-address"]}`}
          >
            {formattedAddress.street}
            <br />
            {formattedAddress.city}, {formattedAddress.province}
            <br />
            {formattedAddress.postalCode}
          </p>
          <p
            className={`col-sm-6 col-md-4 mt-3 text-body-small ${styles["operation-description"]}`}
          >
            Due to the nature of our fully automated booking system, our
            facility is open 24/7. The doors are always locked, and access is
            only permitted to customers with an access code.
          </p>
        </div>

        <div className="d-none d-sm-block mt-5">
          <CTA
            href={locationToUse.endpoints.GOOGLE_MAPS_LOC}
            analytics={analytics}
            target="_blank"
            src="/contact-us"
            dest="google-maps"
          >
            <Picture
              src={settings.ASSET_PATH + locationToUse.map.desktop}
              className={styles["map-image"]}
              alt="Kudos Golf Location"
              title="Kudos Golf Location"
            />
          </CTA>
        </div>
        <div className="d-block d-sm-none mt-5">
          <CTA
            href={locationToUse.endpoints.GOOGLE_MAPS_LOC}
            analytics={analytics}
            target="_blank"
            src="/contact-us"
            dest="google-maps"
          >
            <Picture
              src={settings.ASSET_PATH + locationToUse.map.mobile}
              className={styles["map-image"]}
              alt="Kudos Golf Location"
              title="Kudos Golf Location"
            />
          </CTA>
        </div>
      </section>

      <Footer analytics={analytics} />
    </Page>
  );
};

export default ContactUs;
